<template>
  <div class="exchange-container">
    <span class="card-title" style="margin-bottom: 15px">Visão Geral do Câmbio</span>
    <div class="charts-two-blocks-exchange-container">
      <div class="comparative-fiat-currency-container">
        <div class="chart-custom-header" style="flex-direction: column">
          <span class="card-title">Comparativo entre moedas</span>
          <div class="chart-custom-buttons-container" style="margin: 20px 0; align-self: flex-end">
            <CustomChartButton text="Dia" @click="handleTimeFrameChange('day')" />
            <CustomChartButton text="Semana" @click="handleTimeFrameChange('week')" />
            <CustomChartButton text="Mês" @click="handleTimeFrameChange('month')" />
            <CustomChartButton text="Ano" @click="handleTimeFrameChange('year')" />
          </div>
          <div class="chart-custom-legend-container" style="margin-top: 15px">
            <CustomLegend color="#E31E51" text="Dólar Americano" />
            <CustomLegend color="#73749A" text="Peso Argentino" />
            <CustomLegend color="#E135F0" text="Euro" />
            <CustomLegend color="#324DDD" text="Libra Esterlina" />
          </div>
        </div>
        <CustomSkeleton v-if="isLoadingAllData" type="card" />
        <canvas id="comparative-fiat-currency-chart" class="comparative-fiat-currency-chart" />
      </div>
      <div class="travel-time-chart-container">
        <span class="card-title">Câmbio hoje</span>
        <CustomSkeleton v-if="isLoadingAllData" type="list" />
        <DataTable
          v-if="!isLoadingAllData"
          :value="exchangeLatest"
          responsive-layout="scroll"
          style="margin-top: 30px"
        >
          <Column header="Bandeira" style="width: 40px">
            <template #body="rowData">
              <img :src="rowData.data.flag" alt="Bandeira" style="width: 32px; height: 32px" />
            </template>
          </Column>
          <Column field="name" header="Moeda" />
          <Column field="rates" header="Compra">
            <template #body="rowData">
              {{ formatCurrency(rowData) }}
            </template>
          </Column>
        </DataTable>
      </div>
    </div>

    <div class="comparative-fiat-currency-container">
      <div class="chart-custom-header" style="flex-direction: column">
        <span class="card-title">Variação Cambial vs. Número de Visitantes</span>
        <div class="chart-custom-buttons-container" style="margin: 20px 0; align-self: flex-end">
          <CustomChartButton text="Dia" @click="handleTimeFrameChange2('day')" />
          <CustomChartButton text="Mês" @click="handleTimeFrameChange2('month')" />
          <CustomChartButton text="Ano" @click="handleTimeFrameChange2('year')" />
        </div>
        <div class="chart-custom-legend-container" style="margin-top: 15px">
          <CustomLegend color="#E31E51" text="Dólar Americano" />
          <CustomLegend color="#73749A" text="Peso Argentino" />
          <CustomLegend color="#E135F0" text="Euro" />
          <CustomLegend color="#324DDD" text="Libra Esterlina" />
          <CustomLegend color="#00FF00" text="Visitantes" />
        </div>
      </div>
      <CustomSkeleton v-if="isLoadingAllData" type="card" />
      <canvas id="variation-fiat-currency-chart" class="variation-fiat-currency-chart" />
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { Chart, registerables } from 'chart.js'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import CustomChartButton from '@/Shared/Charts/CustomChartButton'
import CustomLegend from '@/Shared/Charts/CustomLegend'
import CustomSkeleton from '@/Shared/CustomSkeleton.vue'
import moment from 'moment'

Chart.register(...registerables)

export default {
  name: 'Exchange',
  components: {
    CustomChartButton,
    CustomLegend,
    DataTable,
    Column,
    CustomSkeleton,
  },
  props: {
    visitors: {
      type: Object,
      required: true,
    },
    isLoadingAllData: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      exchangeLatest: [],
      exchangeTimeseries: {},
      timeFrame: 'day',
      timeFrame2: 'day',
      isLoadingExchangeLatest: false,
      chart: null,
      chart2: null,
    }
  },
  watch: {
    visitors: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal.dates && newVal.dates.length > 0) {
          this.$nextTick(async () => {
            await this.getExchangeLatest()
            await this.getExchangeTimeseries()
          })
        }
      },
    },
  },
  methods: {
    isEmptyObject(obj) {
      return Object.keys(obj).length === 0 && obj.constructor === Object
    },
    handleTimeFrameChange(timeFrame) {
      this.timeFrame = timeFrame
      this.createComparativeFiatCurrencyChart()
    },
    handleTimeFrameChange2(timeFrame) {
      this.timeFrame2 = timeFrame
      this.createVariationFiatCurrencyChart()
    },
    async getExchangeLatest() {
      try {
        this.isLoadingExchangeLatest = true
        const response = await axios.get('/api/exchange/latest')
        this.exchangeLatest = response.data
      } catch (error) {
        console.error('Error fetching exchange data:', error)
      } finally {
        this.isLoadingExchangeLatest = false
      }
    },
    async getExchangeTimeseries() {
      try {
        const response = await axios.get('/api/exchange/timeseries')
        this.exchangeTimeseries = response.data
        this.createComparativeFiatCurrencyChart()
        this.createVariationFiatCurrencyChart()
      } catch (error) {
        console.error('Error fetching exchange data:', error)
      }
    },
    formatCurrency(rowData) {
      if (rowData && typeof rowData.data.rates === 'number') {
        return `R$ ${rowData.data.rates.toFixed(2)}`
      } else {
        return ''
      }
    },
    createComparativeFiatCurrencyChart() {
      const ctx = document.getElementById('comparative-fiat-currency-chart').getContext('2d')
      const labels = []
      const datasets = []
      const colors = {
        USD: '#E31E51', // Dólar Americano
        ARS: '#73749A', // Peso Argentino
        EUR: '#E135F0', // Euro
        GBP: '#324DDD', // Libra Esterlina
      }

      const timeseries = this.exchangeTimeseries

      for (const currency in timeseries) {
        const rates = timeseries[currency].rates
        const processedData = this.processData(rates, this.timeFrame)
        labels.push(...processedData.labels)
        datasets.push({
          label: currency,
          data: processedData.data,
          borderColor: colors[currency] || this.getRandomColor(),
          fill: false,
        })
      }

      if (this.chart) {
        this.chart.destroy()
      }

      this.chart = new Chart(ctx, {
        type: 'line',
        data: {
          labels: [...new Set(labels)],
          datasets: datasets,
        },
        options: {
          responsive: true,
          spanGaps: true,
          elements: {
            line: {
              tension: 1,
            },
            point: {
              radius: 3,
              hoverRadius: 8,
              hitRadius: 10,
            },
          },
          plugins: {
            tooltip: {
              callbacks: {
                label: function (context) {
                  return `${context.dataset.label}: R$${Number(context.raw).toFixed(2)}`
                },
              },
            },
            legend: {
              display: false,
            },
          },
          scales: {
            x: {
              title: {
                display: false,
              },
            },
            y: {
              beginAtZero: false,
              title: {
                display: false,
              },
              ticks: {
                callback: function (value) {
                  return 'R$' + value.toFixed(2)
                },
              },
            },
          },
        },
      })
    },
    createVariationFiatCurrencyChart() {
      const ctx = document.getElementById('variation-fiat-currency-chart').getContext('2d')
      const labels = []
      const datasets = []
      const colors = {
        USD: '#E31E51', // Dólar Americano
        ARS: '#73749A', // Peso Argentino
        EUR: '#E135F0', // Euro
        GBP: '#324DDD', // Libra Esterlina
        Visitors: '#00FF00', // Visitantes
      }

      const timeseries = this.exchangeTimeseries

      // Processar dados das moedas
      for (const currency in timeseries) {
        const rates = timeseries[currency].rates
        const processedData = this.processData(rates, this.timeFrame2)
        labels.push(...processedData.labels)
        datasets.push({
          label: currency,
          data: processedData.data,
          borderColor: colors[currency] || this.getRandomColor(),
          fill: false,
        })
      }

      // Obter datas únicas das moedas e formatá-las corretamente
      const uniqueLabels = [...new Set(labels)].map((date) =>
        moment(date, 'YYYY/MM/DD').format('DD/MM/YYYY'),
      )

      // Processar dados dos visitantes
      const visitorDates = this.visitors.dates.map((date) => moment(date).format('YYYY-MM-DD'))
      const visitorCounts = this.countVisitorsByDate(visitorDates)

      // Filtrar dados dos visitantes para manter apenas as datas que estão em uniqueLabels
      const filteredVisitorCounts = {
        labels: visitorCounts.labels.filter((date) => {
          return uniqueLabels.includes(moment(date, 'YYYY-MM-DD').format('DD/MM/YYYY'))
        }),
        data: [],
      }

      filteredVisitorCounts.labels.forEach((label) => {
        const index = visitorCounts.labels.indexOf(label)
        filteredVisitorCounts.data.push(visitorCounts.data[index])
      })

      datasets.push({
        label: 'Visitantes',
        data: filteredVisitorCounts.data,
        borderColor: colors.Visitors,
        fill: false,
        yAxisID: 'y2',
      })

      if (this.chart2) {
        this.chart2.destroy()
      }

      this.chart2 = new Chart(ctx, {
        type: 'line',
        data: {
          labels: uniqueLabels,
          datasets: datasets,
        },
        options: {
          responsive: true,
          spanGaps: true,
          elements: {
            line: {
              tension: 1,
            },
            point: {
              radius: 2,
              hoverRadius: 8,
              hitRadius: 10,
            },
          },
          plugins: {
            tooltip: {
              callbacks: {
                label: function (context) {
                  return `${context.dataset.label}: ${
                    context.dataset.label === 'Visitantes'
                      ? context.raw
                      : `R$${Number(context.raw).toFixed(2)}`
                  }`
                },
              },
            },
            legend: {
              display: false,
            },
          },
          scales: {
            x: {
              title: {
                display: false,
              },
            },
            y: {
              beginAtZero: false,
              title: {
                display: false,
              },
              ticks: {
                callback: function (value) {
                  return 'R$' + value.toFixed(2)
                },
              },
            },
            y2: {
              beginAtZero: false,
              position: 'right',
              title: {
                display: false,
              },
              ticks: {
                callback: function (value) {
                  return value.toFixed(0)
                },
              },
              grid: {
                drawOnChartArea: false,
              },
            },
          },
        },
      })
    },

    processData(rates, timeFrame) {
      const dataMap = {}
      const labels = []

      rates.forEach((rate) => {
        const date = this.formatDate(rate.date, timeFrame)
        if (!dataMap[date]) {
          dataMap[date] = []
        }
        dataMap[date].push(rate.rate)
      })

      for (const date in dataMap) {
        const avgRate = dataMap[date].reduce((a, b) => a + b, 0) / dataMap[date].length
        labels.push(date)
        dataMap[date] = avgRate
      }

      return {
        labels: labels,
        data: Object.values(dataMap),
      }
    },
    formatDate(date, timeFrame) {
      switch (timeFrame) {
        case 'day':
          return moment(date).format('YYYY/MM/DD')
        case 'week':
          console.log(date)
          console.log(timeFrame)
          console.log(moment(date).startOf('isoWeek').format('YYYY/WW'))
          return moment(date).startOf('isoWeek').format('YYYY/WW')
        case 'month':
          return moment(date).format('YYYY/MM')
        case 'year':
          return moment(date).format('YYYY')
        default:
          return moment(date).format('YYYY/MM/DD')
      }
    },
    countVisitorsByDate(visitorDates) {
      const dateCount = {}
      visitorDates.forEach((date) => {
        if (!dateCount[date]) {
          dateCount[date] = 0
        }
        dateCount[date]++
      })

      const labels = Object.keys(dateCount)
      const data = labels.map((date) => dateCount[date])

      return {
        labels,
        data,
      }
    },

    getRandomColor() {
      const letters = '0123456789ABCDEF'
      let color = '#'
      for (let i = 0; i < 6; i++) {
        color += letters[Math.floor(Math.random() * 16)]
      }
      return color
    },
  },
}
</script>

<style src="./styles.css"></style>
